import {
  useTable,
  Table,
  List,
  Icons,
  Tag,
  EditButton,
  Form,
  Input,
  Button,
  Row,
  Col,
  Card,
  Space,
  Badge,
} from "@pankod/refine-antd";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { useState } from "react";

import { Features } from "interfaces/features";
import { EditFeatureModal } from "./edit";

export const FeatureManagementList: React.FC<IResourceComponentsProps> = () => {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<
    Features.CategoryItem | undefined
  >();

  const { tableProps, searchFormProps } = useTable<Features.CategoryItem>({
    resource: "feature-management",
    onSearch: (values: any) => {
      return [
        {
          field: "search",
          operator: "contains",
          value: values.search,
        },
      ];
    },
  });

  const handleEditClick = (record: Features.CategoryItem) => {
    setSelectedCategory(record);
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    // Table will automatically refresh after successful update
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setSelectedCategory(undefined);
  };

  return (
    <>
      <List title="Özellik Yönetimi">
        <Card style={{ marginBottom: 16 }}>
          <Form
            {...searchFormProps}
            layout="vertical"
            style={{ marginBottom: 0 }}
          >
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={24} md={12} lg={10} xl={8}>
                <Form.Item
                  name="search"
                  label="Arama"
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder="Kategori adı ara..."
                    allowClear
                    prefix={<Icons.SearchOutlined />}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={14} xl={16}>
                <Space
                  size="middle"
                  style={{ width: "100%", justifyContent: "flex-end" }}
                >
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<Icons.SearchOutlined />}
                    size="large"
                  >
                    Ara
                  </Button>
                  <Button
                    onClick={() => searchFormProps.form?.resetFields()}
                    icon={<Icons.ReloadOutlined />}
                    size="large"
                  >
                    Temizle
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Card>

        <Card>
          <Table
            {...tableProps}
            scroll={{ x: "max-content" }}
            pagination={{
              ...tableProps.pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} / ${total} kayıt`,
            }}
          >
            <Table.Column<Features.CategoryItem>
              dataIndex="categoryName"
              title="Kategori Adı"
              width={200}
              fixed="left"
              ellipsis={{ showTitle: false }}
            />
            <Table.Column<Features.CategoryItem>
              dataIndex="features"
              title="Özellikler"
              width={500}
              render={(features: Features.FeatureVisibility[]) => {
                if (!features || features.length === 0) {
                  return <span style={{ color: "#999" }}>-</span>;
                }

                const maxVisible = 4;
                const visibleFeatures = features.slice(0, maxVisible);
                const remainingCount = features.length - maxVisible;

                return (
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}
                  >
                    {visibleFeatures.map((feature, index) => (
                      <Tag
                        key={index}
                        color={feature.isVisible ? "green" : "red"}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          margin: 0,
                          fontSize: "12px",
                          padding: "2px 8px",
                        }}
                      >
                        <span>{feature.name}</span>
                        <span style={{ fontSize: "10px", opacity: 0.8 }}>
                          ({feature.isVisible ? "Aktif" : "Deaktif"})
                        </span>
                      </Tag>
                    ))}
                    {remainingCount > 0 && (
                      <Tag
                        color="default"
                        style={{ fontSize: "12px", margin: 0 }}
                      >
                        +{remainingCount} özellik daha
                      </Tag>
                    )}
                  </div>
                );
              }}
            />
            <Table.Column<Features.CategoryItem>
              dataIndex="features"
              title="İstatistikler"
              width={200}
              render={(features: Features.FeatureVisibility[]) => {
                if (!features || features.length === 0) {
                  return <span style={{ color: "#999" }}>-</span>;
                }

                const activeCount = features.filter((f) => f.isVisible).length;
                const inactiveCount = features.filter(
                  (f) => !f.isVisible
                ).length;

                return (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Tag color="green" style={{ margin: 0 }}>
                      {activeCount} Aktif
                    </Tag>
                    <Tag color="red" style={{ margin: 0 }}>
                      {inactiveCount} Deaktif
                    </Tag>
                  </div>
                );
              }}
            />
            <Table.Column<Features.CategoryItem>
              dataIndex="actions"
              title="İşlemler"
              width={100}
              fixed="right"
              render={(_, record) => (
                <EditButton
                  size="small"
                  onClick={() => handleEditClick(record)}
                  style={{ margin: 0 }}
                >
                  Düzenle
                </EditButton>
              )}
            />
          </Table>
        </Card>
      </List>

      <EditFeatureModal
        visible={editModalVisible}
        onCancel={handleEditCancel}
        onSuccess={handleEditSuccess}
        categoryData={selectedCategory}
      />
    </>
  );
};
